import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import { roundRobin } from "./roundRobin";

/**
 * Round-robin for DOUBLES pairs. 
 * If the season has more weeks (allDays) than the round-robin produces, it cycles back to round 1.
 *
 * @param {string} pennantId - The ID of the pennant (to fetch pairs).
 * @param {Date[]} allDays - An array of valid match days in the season.
 * @returns {Promise<Array>} An array of weekly draw objects (one per date).
 */
export default async function generateDoublesDraw(pennantId, allDays) {
  const teamsSnap = await getDocs(collection(db, "Pennants", pennantId, "teams"));
  if (teamsSnap.empty) {
    alert("No doubles pairs created. Please create pairs first.");
    return [];
  }

  const doublesPairs = teamsSnap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  // Group pairs by section
  const pairsBySection = {};
  doublesPairs.forEach((pair) => {
    let pairSection = pair.section;
    if (!pairSection) {
      const mems = Object.values(pair.members || {});
      if (mems.length > 0) {
        pairSection = mems[0].section || "Unknown";
      } else {
        pairSection = "Unknown";
      }
    }
    if (!pairsBySection[pairSection]) {
      pairsBySection[pairSection] = [];
    }
    pairsBySection[pairSection].push(pair);
  });

  // Helper to form a name if the doc doesn't have .name
  const getDoublesPairName = (pairDoc) => {
    if (pairDoc.name) return pairDoc.name;
    const mems = Object.values(pairDoc.members || {});
    const lastNames = mems.map((m) => m.lastName || "Member");
    return lastNames.join("/");
  };

  const allSectionDraws = [];

  // For each section, produce a round-robin
  Object.entries(pairsBySection).forEach(([sectionKey, sectionPairs]) => {
    if (sectionPairs.length < 2) return; // skip if not enough pairs

    // Build array for roundRobin
    const rrInput = sectionPairs.map((pair) => ({
      ...pair,
      name: getDoublesPairName(pair),
      section: pair.section || sectionKey,
    }));

    const rounds = roundRobin(rrInput);
    const totalRounds = rounds.length;

    // Cycle through allDays, repeating the rounds if needed
    for (let dayIdx = 0; dayIdx < allDays.length; dayIdx+= 1) {
      const date = allDays[dayIdx].toLocaleDateString();
      const roundIndex = dayIdx % totalRounds;
      const matchups = rounds[roundIndex];

      const drawMatches = matchups.map(({ home, away }) => {
        if (!away || away.id === null) {
          // "Bye"
          return {
            team1: {
              name: home.name,
              id: home.id,
              section: home.section || sectionKey,
            },
            team2: {
              name: "Bye",
              id: null,
              section: home.section || sectionKey,
            },
          };
        }
        // Normal matchup
        return {
          team1: {
            name: home.name,
            id: home.id,
            section: home.section || sectionKey,
          },
          team2: {
            name: away.name,
            id: away.id,
            section: away.section || sectionKey,
          },
        };
      });

      allSectionDraws.push({
        section: sectionKey,
        week: dayIdx + 1,
        date,
        matches: drawMatches,
        id: `${sectionKey}_week-${dayIdx + 1}`,
      });
    }
  });

  return allSectionDraws;
}
